/**
 * The cookie that tells the server which language to render in.
 *
 * The visitor's choice lives in the preference store, which is `localStorage`,
 * which the server cannot see. Rendering the page in the visitor's language
 * needs it before a byte of HTML is written, so it is mirrored here — a locale
 * code and nothing else, read for nothing else, and necessary in the sense the
 * consent surface uses the word: the page cannot be served in the right
 * language without it.
 *
 * It sits in `support/` beside the registry because the value it may carry is
 * the registry's to decide, and because the store that writes it and the server
 * that reads it must agree on the name.
 */

import { isCoveredLocale } from "./locales";

/** The cookie's name, written by the client and read by the server. */
export const LOCALE_COOKIE = "locale";

/** A year. Long enough that a visitor is not asked again by forgetting. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/**
 * What to assign to `document.cookie` for a chosen locale, or the assignment
 * that removes the cookie when the choice is `""` — the visitor giving the
 * decision back to their browser, which the server can only honour by not
 * finding a cookie at all.
 *
 * `SameSite=Lax` because the cookie is only ever read on a top-level document
 * request, and `Secure` only where the page is served over TLS: the local
 * stack the e2e suites drive is not, and a `Secure` cookie there is silently
 * dropped.
 */
export function localeCookieAssignment(code: string, secure: boolean): string {
  const attributes = `Path=/; SameSite=Lax${secure ? "; Secure" : ""}`;
  if (code === "" || !isCoveredLocale(code)) {
    return `${LOCALE_COOKIE}=; ${attributes}; Max-Age=0`;
  }
  return `${LOCALE_COOKIE}=${code}; ${attributes}; Max-Age=${LOCALE_COOKIE_MAX_AGE}`;
}
